import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import {
  EnvelopeIcon,
  PaperAirplaneIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { HipaaAuthorizationSendSchema } from '@/services/api/generated/hipaaESignService';

type DeliveryMethod = 'sms' | 'email';

interface HipaaESignRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (params: HipaaAuthorizationSendSchema) => Promise<void>;
  isSubmitting?: boolean;
  clientName?: string | null;
  clientEmail?: string | null;
  clientPhone?: string | null;
  /** Firm name for SMS message template */
  firmName?: string | null;
  /** Whether direct client contact is allowed for this matter */
  canContactClient?: boolean;
  /** Z-index for the modal (use Z_INDEX_MODAL_NESTED for nested modals) */
  zIndex?: number;
}

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export const HipaaESignRequestModal: React.FC<HipaaESignRequestModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  clientName,
  clientEmail,
  clientPhone,
  firmName,
  canContactClient = true,
  zIndex,
}) => {
  // Build default SMS message with firm name
  const defaultSmsMessage = firmName
    ? `This is Finch Records Services reaching out on behalf of ${firmName} to request your signature on this HIPAA Authorization `
    : '';

  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>('sms');
  const [phoneNumber, setPhoneNumber] = useState(clientPhone ?? '');
  const [email, setEmail] = useState(clientEmail ?? '');
  const [showCustomMessage, setShowCustomMessage] = useState(!!firmName);
  const [customMessage, setCustomMessage] = useState(defaultSmsMessage);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const prevIsOpen = useRef(isOpen);

  // Handle success/error state timing
  useEffect(() => {
    if (submitStatus === 'success') {
      const timer = setTimeout(() => {
        onClose();
        // Reset after close animation
        setTimeout(() => setSubmitStatus('idle'), 200);
      }, 1000);
      return () => clearTimeout(timer);
    }
    if (submitStatus === 'error') {
      const timer = setTimeout(() => {
        setSubmitStatus('idle');
      }, 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [submitStatus, onClose]);

  // Reset state only when modal transitions from closed to open
  useEffect(() => {
    if (isOpen && !prevIsOpen.current) {
      // Modal just opened - reset state
      setSubmitStatus('idle');
      setPhoneNumber(clientPhone ?? '');
      setEmail(clientEmail ?? '');
      setShowCustomMessage(!!firmName);
      setCustomMessage(defaultSmsMessage);
    }
    prevIsOpen.current = isOpen;
  }, [isOpen, clientPhone, clientEmail, firmName, defaultSmsMessage]);

  // Update SMS message when firmName becomes available (async load)
  useEffect(() => {
    if (firmName && !customMessage) {
      setCustomMessage(defaultSmsMessage);
      setShowCustomMessage(true);
    }
  }, [firmName, defaultSmsMessage, customMessage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canContactClient) return;

    setSubmitStatus('submitting');
    try {
      await onSubmit({
        client_name: clientName,
        client_email: email, // Always required - Adobe Sign needs email for document delivery
        client_phone_number: deliveryMethod === 'sms' ? phoneNumber : null,
        send_sms: deliveryMethod === 'sms' && !!phoneNumber,
        sms_message: showCustomMessage && customMessage ? customMessage : null,
      });
      setSubmitStatus('success');
    } catch {
      setSubmitStatus('error');
    }
  };

  const handleClose = () => {
    if (submitStatus === 'submitting') return; // Prevent closing while submitting
    setPhoneNumber(clientPhone ?? '');
    setEmail(clientEmail ?? '');
    setShowCustomMessage(false);
    setCustomMessage('');
    setSubmitStatus('idle');
    onClose();
  };

  const isValidPhone = phoneNumber.replace(/\D/g, '').length >= 10;
  const isValidEmail = email.includes('@') && email.includes('.');
  // Email is always required; phone is required only for SMS delivery
  const isDeliveryValid =
    isValidEmail && (deliveryMethod === 'sms' ? isValidPhone : true);
  const isButtonBusy =
    submitStatus === 'submitting' ||
    submitStatus === 'success' ||
    submitStatus === 'error';
  const canSubmit = canContactClient && isDeliveryValid && !isButtonBusy;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" zIndex={zIndex}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <PaperAirplaneIcon className="h-4 w-4 text-indigo-600" />
            </div>
            Request E-Signature
          </DialogTitle>
          <DialogDescription>
            Send a Finch-branded HIPAA Authorization to the client to sign via
            Adobe Sign.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Client Contact Restriction Warning */}
          {!canContactClient && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800">
                  Client contact is restricted
                </p>
                <p className="text-amber-700 mt-0.5">
                  Direct client contact is disabled for this matter. Only the
                  firm can communicate with the client.
                </p>
              </div>
            </div>
          )}

          {/* Delivery Method Toggle */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Delivery Method</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setDeliveryMethod('sms')}
                className={cn(
                  'flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all',
                  deliveryMethod === 'sms'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300',
                )}
              >
                <PhoneIcon className="h-4 w-4" />
                <span className="font-medium">SMS</span>
              </button>
              <button
                type="button"
                onClick={() => setDeliveryMethod('email')}
                className={cn(
                  'flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all',
                  deliveryMethod === 'email'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300',
                )}
              >
                <EnvelopeIcon className="h-4 w-4" />
                <span className="font-medium">Email</span>
              </button>
            </div>
          </div>

          {/* Contact Input - Email is always required */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">
              Email Address <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="client@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {!isValidEmail && (
              <p className="text-xs text-gray-500">
                Required for Adobe Sign document delivery
              </p>
            )}
          </div>

          {/* Phone Input - Only shown for SMS delivery */}
          {deliveryMethod === 'sms' && (
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium">
                Phone Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phone"
                type="tel"
                placeholder="(555) 123-4567"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
              {phoneNumber && !isValidPhone && (
                <p className="text-xs text-amber-600">
                  Please enter a valid 10-digit phone number
                </p>
              )}
            </div>
          )}

          {/* Customize Message (SMS only) */}
          {deliveryMethod === 'sms' && (
            <div className="border rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowCustomMessage(!showCustomMessage)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <span className="font-medium">Customize SMS message</span>
                {showCustomMessage ? (
                  <ChevronUpIcon className="h-4 w-4" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4" />
                )}
              </button>
              {showCustomMessage && (
                <div className="px-3 pb-3 pt-1 border-t bg-gray-50">
                  <Textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Enter a custom message to include with the signing link..."
                    className="text-sm resize-none"
                    rows={3}
                  />
                  <p className="text-xs text-gray-500 mt-1.5">
                    Leave blank to use the default message.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isButtonBusy}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              className={cn(
                'gap-2 transition-colors',
                submitStatus === 'success' &&
                  'bg-green-600 hover:bg-green-600 text-white',
                submitStatus === 'error' &&
                  'bg-red-600 hover:bg-red-600 text-white',
              )}
            >
              {submitStatus === 'submitting' ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Sending...
                </>
              ) : submitStatus === 'success' ? (
                <>
                  <CheckIcon className="h-4 w-4" />
                  Sent!
                </>
              ) : submitStatus === 'error' ? (
                <>
                  <XMarkIcon className="h-4 w-4" />
                  Failed
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="h-4 w-4" />
                  Send Request
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
